import type { ReactNode } from 'react';
import { PodApiAuthenticationFailed } from 'components/AuthenticationFailed';
import React, { createContext, useContext, useEffect, useMemo } from 'react';
import { useWorkspace } from 'wrappers/WorkspaceProvider';
import { useAuth0 } from '@auth0/auth0-react';

type Props = {
  children: ReactNode;
};

type PodApiAccessTokenContext = {
  customerApiToken: string | null;
  internalApiToken: string | null;
};

const defaultContext = {
  customerApiToken: null,
  internalApiToken: null,
};

export const PodApiAccessToken =
  createContext<PodApiAccessTokenContext>(defaultContext);

type PodTokenResult =
  | {
      status: 'ready';
      customerApiToken: string;
      internalApiToken: string;
    }
  | { status: 'failed' };

// Kept outside React so the suspense retry can render the tokens without
// mounting another loader.
const podTokens = new Map<string, PodTokenResult>();
const podTokenRequests = new Map<string, Promise<void>>();

const requestPodTokens = (
  cacheKey: string,
  request: () => Promise<PodTokenResult>
): never => {
  const inflight = podTokenRequests.get(cacheKey);
  if (inflight) throw inflight;
  const promise = request().then((result) => {
    podTokens.set(cacheKey, result);
    podTokenRequests.delete(cacheKey);
  });
  podTokenRequests.set(cacheKey, promise);
  throw promise;
};

export const PodApiAccessTokenProvider = ({ children }: Props) => {
  const { workspace } = useWorkspace();
  const { user, isAuthenticated, getAccessTokenSilently } = useAuth0();
  const workspaceId = workspace?.id;
  const cacheKey =
    user?.sub && workspaceId ? `${user.sub}:${workspaceId}` : undefined;
  const cached = cacheKey ? podTokens.get(cacheKey) : undefined;
  const isFailed = cached?.status === 'failed';
  const internalAudience = workspace?.pod.internalApiAudience;
  const customerAudience = workspace?.pod.customerApiAudience;

  // A failure is cleared once its screen unmounts, so the next mount retries.
  useEffect(
    () => () => {
      if (cacheKey && isFailed) podTokens.delete(cacheKey);
    },
    [cacheKey, isFailed]
  );

  const context: PodApiAccessTokenContext = useMemo(
    () => ({
      customerApiToken:
        cached?.status === 'ready' ? cached.customerApiToken : null,
      internalApiToken:
        cached?.status === 'ready' ? cached.internalApiToken : null,
    }),
    [cached]
  );

  if (isFailed) {
    return <PodApiAuthenticationFailed />;
  }

  if (
    isAuthenticated &&
    cacheKey &&
    internalAudience &&
    customerAudience &&
    !cached
  ) {
    requestPodTokens(cacheKey, async () => {
      try {
        const [internalApiToken, customerApiToken] = await Promise.all([
          getAccessTokenSilently({
            authorizationParams: { audience: internalAudience },
          }),
          getAccessTokenSilently({
            authorizationParams: { audience: customerAudience },
          }),
        ]);
        return { status: 'ready', customerApiToken, internalApiToken };
      } catch {
        return { status: 'failed' };
      }
    });
  }

  return (
    <PodApiAccessToken.Provider value={context}>
      {children}
    </PodApiAccessToken.Provider>
  );
};

export const usePodApiAccessTokenContext = () => useContext(PodApiAccessToken);
